import axios from 'axios'
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { extractErrorMessage } from '../../utils/apitools'
import useAccount from '../useAccount'
import { skyProductURL, skyQueryKey } from './skyEndpoints'
import type { AutoCoderFramework, AutoCoderPipelineRecord, MultiPipelineRecordsResponse } from '../../types/gotypes'
import type { LabelQuery } from '../../components/social-media/types'

export interface UseSkyCommentsResult {
  comments: AutoCoderPipelineRecord[]
  frameworksMap: Record<string, AutoCoderFramework[]>
  totalCount: number
  page: number
  rowsPerPage: number
  searchText: string
  setPage: (page: number) => void
  setRowsPerPage: (rows: number) => void
  setSearchText: (text: string) => void
  isLoading: boolean
  error: Error | null
}

/**
 * The coded comment stream for one product, paged and searchable.
 *
 * IT OWNS ITS OWN PAGING STATE. A page that had to thread page/rowsPerPage
 * through props would be doing query work, which is precisely what pages under
 * `pages/sky-social-media/` must not contain — so the state lives with the
 * request that consumes it.
 *
 * `enabled` is the deferred-fetch lever: the comment stream sits behind a
 * collapsed section on every dashboard, and there is no reason to pull a page
 * of records for a section nobody has opened. Pass the section's expanded flag.
 */
export function useSkyComments(
  product: string,
  enabled = true,
  labelQuery?: LabelQuery,
): UseSkyCommentsResult {
  const account = useAccount()
  const labelsKey = useMemo(() => JSON.stringify(labelQuery ?? null), [labelQuery])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [searchText, setSearchTextRaw] = useState('')

  // A search that kept the current page would ask for page 4 of a result set
  // that may now have one page, and render an empty table over a live query.
  const setSearchText = (text: string) => {
    setSearchTextRaw(text)
    setPage(0)
  }

  const query = useQuery({
    queryKey: skyQueryKey(product, 'comments', { searchText, page, rowsPerPage, labelsKey }),
    enabled: Boolean(product && account.user && enabled),
    retry: 1,
    retryDelay: 1000,
    queryFn: async ({ signal }): Promise<MultiPipelineRecordsResponse> => {
      try {
        const res = await axios.post<MultiPipelineRecordsResponse>(
          skyProductURL(product, '/pipelines/records'),
          {
            search_text: searchText,
            labelQuery,
            state: 'completed',
            limit: rowsPerPage,
            offset: page * rowsPerPage,
          },
          // Typing in the search box supersedes the previous request.
          { signal },
        )
        return res.data
      } catch (err) {
        throw new Error(extractErrorMessage(err))
      }
    },
  })

  const frameworksMap = useMemo(
    () => (query.data?.frameworks_map ?? {}) as Record<string, AutoCoderFramework[]>,
    [query.data],
  )

  return {
    comments: query.data?.records ?? [],
    frameworksMap,
    totalCount: query.data?.total_count ?? 0,
    page,
    rowsPerPage,
    searchText,
    setPage,
    setRowsPerPage,
    setSearchText,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  }
}

export default useSkyComments
